import React, { useState, useRef, useCallback } from "react";
import styled from "styled-components";
import { UploadOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { message } from "antd";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../helpers/firebase";
import { device } from "../../lib/breakpoints";

const ImageUpload = ({ onImagesChange, maxImages = 3, disabled = false }) => {
  const [images, setImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = useCallback(
    async (files) => {
      if (disabled || isUploading) return;

      console.log("handleFileSelect called with files:", files);

      const fileArray = Array.from(files);
      console.log("File array:", fileArray);

      const validFiles = fileArray.filter((file) => {
        console.log(
          "Checking file:",
          file.name,
          "Type:",
          file.type,
          "Size:",
          file.size
        );

        // Check file type
        if (!file.type.startsWith("image/")) {
          console.log("Invalid file type:", file.type);
          message.error(`${file.name} is not an image file`);
          return false;
        }

        // Check file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          console.log("File too large:", file.size);
          message.error(`${file.name} is too large. Maximum size is 5MB`);
          return false;
        }

        console.log("File is valid:", file.name);
        return true;
      });

      console.log("Valid files:", validFiles.length);

      if (validFiles.length === 0) {
        console.log("No valid files to upload");
        return;
      }

      // Check if adding these files would exceed the limit
      if (images.length + validFiles.length > maxImages) {
        message.error(
          `You can only upload up to ${maxImages} images at a time`
        );
        return;
      }

      console.log("Starting upload process...");
      setIsUploading(true);

      try {
        console.log("Starting upload for", validFiles.length, "files");
        const uploadPromises = validFiles.map(async (file) => {
          console.log("Uploading file:", file.name, "Size:", file.size);

          try {
            // Create a reference to the file location
            const fileName = `${Date.now()}_${Math.random()
              .toString(36)
              .substr(2, 9)}_${file.name}`;
            const storageRef = ref(storage, `tenant-chat-images/${fileName}`);

            console.log("Uploading to path:", storageRef.fullPath);

            // Upload the file
            const snapshot = await uploadBytes(storageRef, file);
            console.log("Upload completed, getting download URL...");

            // Get the download URL
            const imageUrl = await getDownloadURL(snapshot.ref);
            console.log(
              "File uploaded successfully:",
              file.name,
              "URL:",
              imageUrl
            );

            return {
              id: Date.now() + Math.random(),
              file,
              url: imageUrl,
              name: file.name,
              size: file.size,
            };
          } catch (error) {
            console.error("Error uploading individual file:", file.name, error);
            throw error;
          }
        });

        const uploadedImages = await Promise.all(uploadPromises);
        console.log("All uploads completed:", uploadedImages.length, "images");
        const newImages = [...images, ...uploadedImages];

        setImages(newImages);
        onImagesChange(newImages);

        message.success(
          `${uploadedImages.length} image(s) uploaded successfully`
        );
      } catch (error) {
        console.error("Error uploading images:", error);
        message.error(`Failed to upload images: ${error.message}`);
      } finally {
        setIsUploading(false);
      }
    },
    [images, maxImages, disabled, isUploading, onImagesChange]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (disabled || isUploading) return;

      const files = e.dataTransfer.files;
      handleFileSelect(files);
    },
    [disabled, isUploading, handleFileSelect]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleFileInputChange = useCallback(
    (e) => {
      const files = e.target.files;
      handleFileSelect(files);
      // Reset input value so the same file can be selected again
      e.target.value = "";
    },
    [handleFileSelect]
  );

  const removeImage = useCallback(
    (imageId) => {
      const newImages = images.filter((img) => img.id !== imageId);
      setImages(newImages);
      onImagesChange(newImages);
    },
    [images, onImagesChange]
  );

  const openFileDialog = useCallback(() => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click();
    }
  }, [disabled, isUploading]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <ImageUploadContainer>
      <DropZone
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onClick={openFileDialog}
        $disabled={disabled || isUploading}
        $isUploading={isUploading}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileInputChange}
          style={{ display: "none" }}
          disabled={disabled || isUploading}
        />

        <UploadContent>
          <UploadIcon $isUploading={isUploading}>
            <UploadOutlined />
          </UploadIcon>
          <UploadText>
            {isUploading ? "Uploading..." : "Click or drag images here"}
          </UploadText>
          <UploadSubtext>Up to {maxImages} images, max 5MB each</UploadSubtext>
        </UploadContent>
      </DropZone>

      {images.length > 0 && (
        <ImagePreviewContainer>
          <ImagePreviewGrid>
            {images.map((image) => (
              <ImagePreview key={image.id}>
                <ImageThumbnail
                  src={image.url}
                  alt={image.name}
                  onClick={() => window.open(image.url, "_blank")}
                />
                <ImageInfo>
                  <ImageName title={image.name}>
                    {image.name.length > 15
                      ? `${image.name.substring(0, 15)}...`
                      : image.name}
                  </ImageName>
                  <ImageSize>{formatFileSize(image.size)}</ImageSize>
                </ImageInfo>
                <ImageActions>
                  <ActionButton
                    onClick={() => window.open(image.url, "_blank")}
                    title="View full size"
                  >
                    <EyeOutlined />
                  </ActionButton>
                  <ActionButton
                    onClick={() => removeImage(image.id)}
                    title="Remove image"
                    $danger
                  >
                    <DeleteOutlined />
                  </ActionButton>
                </ImageActions>
              </ImagePreview>
            ))}
          </ImagePreviewGrid>
        </ImagePreviewContainer>
      )}
    </ImageUploadContainer>
  );
};

// Styled Components
const ImageUploadContainer = styled.div`
  width: 100%;
`;

const DropZone = styled.div`
  border: 2px dashed ${(props) => (props.$disabled ? "#d1d5db" : "#cbd5e1")};
  border-radius: 8px;
  padding: 1.5rem;
  text-align: center;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;
  background: ${(props) => (props.$isUploading ? "#f8fafc" : "#ffffff")};

  &:hover {
    border-color: ${(props) =>
      props.$disabled ? "#d1d5db" : "var(--color-primary)"};
    background: ${(props) => (props.$disabled ? "#ffffff" : "#f8fafc")};
  }

  &:active {
    transform: ${(props) => (props.$disabled ? "none" : "scale(0.98)")};
  }
`;

const UploadContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`;

const UploadIcon = styled.div`
  font-size: 2rem;
  color: ${(props) =>
    props.$isUploading ? "#9ca3af" : "var(--color-primary)"};
  transition: color 0.2s ease;
`;

const UploadText = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  color: #374151;
`;

const UploadSubtext = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;
`;

const ImagePreviewContainer = styled.div`
  margin-top: 1rem;
`;

const ImagePreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 0.75rem;

  @media ${device.mobile} {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 0.5rem;
  }
`;

const ImagePreview = styled.div`
  position: relative;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ImageThumbnail = styled.img`
  width: 100%;
  height: 80px;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const ImageInfo = styled.div`
  padding: 0.5rem;
  background: #f9fafb;
`;

const ImageName = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ImageSize = styled.div`
  font-size: 0.7rem;
  color: #9ca3af;
`;

const ImageActions = styled.div`
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  display: flex;
  gap: 0.25rem;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${ImagePreview}:hover & {
    opacity: 1;
  }
`;

const ActionButton = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 4px;
  background: ${(props) => (props.$danger ? "#ef4444" : "rgba(0, 0, 0, 0.7)")};
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.$danger ? "#dc2626" : "rgba(0, 0, 0, 0.9)"};
    transform: scale(1.1);
  }
`;

export default ImageUpload;
